import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "../../components/TopBar";
import { api, fmtUSD } from "../../lib/api";
import { useYear } from "../../context/YearContext";
import { useBusiness } from "../../context/BusinessContext";
import { Receipt, ClipboardList, Package, DollarSign, AlertCircle } from "lucide-react";

export default function BusinessDashboard({ businessId, type, extraResource }) {
  const navigate = useNavigate();
  const { year } = useYear();
  const { activeBusiness } = useBusiness();
  const [stats, setStats] = useState({ revenue: 0, outstanding: 0, jobs_open: 0, jobs_done: 0, expenses: 0 });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      const [inv, exp, res] = await Promise.all([
        api.get(`/invoices?business_id=${businessId}&year=${year}`),
        api.get(`/expenses?year=${year}`),
        extraResource ? api.get(`/${extraResource}?business_id=${businessId}&year=${year}`) : Promise.resolve({ data: [] }),
      ]);
      const expenses = exp.data.filter((e) => (e.business_id || "farm") === businessId);
      const revenue = inv.data.filter((i) => i.status === "paid").reduce((s, i) => s + (i.total || 0), 0);
      const outstanding = inv.data.filter((i) => ["sent", "overdue"].includes(i.status)).reduce((s, i) => s + (i.total || 0), 0);
      const openJobs = res.data.filter((r) => !["delivered", "billed", "paid", "done"].includes(r.status)).length;
      const doneJobs = res.data.length - openJobs;
      const expenseTotal = expenses.reduce((s, e) => s + (e.amount || 0), 0);
      setStats({ revenue, outstanding, jobs_open: openJobs, jobs_done: doneJobs, expenses: expenseTotal });
      setRecent(inv.data.slice(0, 5));
    })();
  }, [businessId, year, extraResource]);

  const net = stats.revenue - stats.expenses;
  const jobLabel = type === "repair" ? "Work Orders" : "Loads";
  const JobIcon = type === "repair" ? ClipboardList : Package;
  const newJobPath = type === "repair" ? "work-orders" : "loads";

  return (
    <div>
      <TopBar
        title={(activeBusiness && activeBusiness.name) || "Dashboard"}
        subtitle={`Snapshot of your ${type} business for ${year}.`}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid={`${type}-dashboard-stats`}>
        <Stat label="Paid Revenue" value={fmtUSD(stats.revenue)} icon={DollarSign} tone="positive" />
        <Stat label="Outstanding" value={fmtUSD(stats.outstanding)} icon={AlertCircle} tone="warning" />
        <Stat label="Expenses" value={fmtUSD(stats.expenses)} icon={Receipt} tone="muted" />
        <Stat label="Net" value={fmtUSD(net)} icon={DollarSign} tone={net >= 0 ? "highlight" : "negative"} testid={`${type}-net`} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <button data-testid={`open-${jobLabel.toLowerCase().replace(" ", "-")}`} onClick={() => navigate(newJobPath)} className="bg-white border border-[#1F1E1B] p-5 hover:bg-[#F5F4F0] text-left">
          <div className="flex items-center gap-3">
            <JobIcon className="w-6 h-6" />
            <div>
              <div className="font-display text-xl font-bold">{stats.jobs_open} open {jobLabel.toLowerCase()}</div>
              <div className="text-xs text-[#66645E]">{stats.jobs_done} completed this year</div>
            </div>
          </div>
        </button>
        <button data-testid="open-invoices" onClick={() => navigate("invoices")} className="bg-white border border-[#1F1E1B] p-5 hover:bg-[#F5F4F0] text-left">
          <div className="flex items-center gap-3">
            <Receipt className="w-6 h-6" />
            <div>
              <div className="font-display text-xl font-bold">{recent.length} recent invoices</div>
              <div className="text-xs text-[#66645E]">Click to manage</div>
            </div>
          </div>
        </button>
      </div>

      {recent.length > 0 && (
        <div className="bg-white border border-[#1F1E1B] p-4" data-testid="recent-invoices">
          <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#66645E] mb-3">Recent Invoices</div>
          <div className="space-y-2">
            {recent.map((r) => (
              <div key={r.id} className="flex items-baseline justify-between text-sm py-1 border-b border-dashed border-[#E5E2D6] last:border-b-0">
                <div>
                  <span className="font-bold">{r.invoice_number}</span>
                  <span className="text-[#66645E] ml-2">{r.customer_name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] uppercase tracking-wider text-[#66645E]">{r.status}</span>
                  <span className="font-bold tabular">{fmtUSD(r.total)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone, testid }) {
  const color = tone === "positive" ? "text-[#1A4135]" : tone === "warning" ? "text-[#BC4727]" : tone === "negative" ? "text-[#BC4727]" : tone === "muted" ? "text-[#66645E]" : "text-[#1F1E1B]";
  return (
    <div className="bg-white border border-[#1F1E1B] p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">{label}</div>
        <Icon className="w-4 h-4 text-[#66645E]" />
      </div>
      <div className={`font-display text-2xl font-black tabular mt-1 ${color}`}>{value}</div>
    </div>
  );
}
